import React from 'react'
import PropTypes from 'prop-types'
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts'
import { validateKeys } from 'utils/validators'
import { PUSH } from 'layers/navigation/store/types'
import { TABLE_URL_PARAMS } from 'store/reportsManager/constants'
import AdminDonutTooltip from 'components/AdminDonutTooltip'
import nextArrowIcon from 'static/img/nextArrow.svg'
import {
  ChartWrapper,
  CompletedText,
  CompletedTextAlt,
  DonutWrapper,
  PercentText,
  StudentActivitiesCompleteWrapper,
  StudentActivitiesEmptyMessageWrapper,
  StudentActivitiesEmptyStateWrapper,
  StudentActivitiesText,
  StyledBullet,
  TextWrapper,
  UserCount,
  UserCountAfter,
  UserCountArrow,
  UserCountButton,
  UserCountSeparator,
  YellowBullet,
} from './component.styles'
import { STUDENT_ACTIVITIES_EMPTY_STATE_MESSAGE } from './constants'
import { programMap } from './programMap'

const AdminDonutChart = ({
  data,
  isOptedInStudentActivities,
  navigationEvent,
  programName,
  siteId,
}) => {
  if (!data || Object.entries(data).length === 0) {
    return null
  }
  const program = programMap?.[programName]
  const dataIsValid = validateKeys(data, program?.keys)

  if (!dataIsValid) {
    console.error('Data is invalid.')
    return null
  }

  const isStudentActivities = programName === 'Student Activities'
  const isK8Tier2 = programName === 'K-8 Tier 2 Grade Band'

  const createCompletedText = (dataSet, programName) => {
    if (programName === 'Student Activities') {
      return `${programName} Complete`
    }
    if (programName === 'K-8 Tier 2 Grade Band') {
      return `Lessons Complete`
    }
    return `**${dataSet?.totalPracticesOrActivitiesCompleted} of ${dataSet?.totalPracticesOrActivities}** ${programName} Complete`
  }

  const createPercentText = (dataSet, programName) => {
    if (programName === 'Student Activities') {
      return dataSet?.totalPracticesOrActivitiesCompleted
    }
    if (programName === 'K-8 Tier 2 Grade Band') {
      return dataSet?.lessonsCompleted || 0
    }
    return dataSet?.totalPracticesOrActivities === 0
      ? `0%`
      : `${Math.round(
          (dataSet?.totalPracticesOrActivitiesCompleted /
            dataSet?.totalPracticesOrActivities) *
            100,
        )}%`
  }

  let numberOfGradesWithProgress = 0
  const createPieData = (dataSet, programObj) => {
    let pieData = []
    programObj.keys.forEach(key => {
      if (isK8Tier2) {
        if (key !== 'totalUsers' && key !== 'groupsCount') {
          pieData.push({ name: key, value: dataSet[key] || 0 })
        }
      } else if (!isStudentActivities) {
        if (key !== 'totalUsers') {
          pieData.push({ name: key, value: dataSet[key] })
        }
      } else if (isStudentActivities) {
        if (
          key === 'totalPracticesOrActivitiesCompleted' &&
          dataSet[key] === 0
        ) {
          pieData.push({ name: key, value: 1 })
        }
        if (
          key !== 'totalUsers' &&
          key !== 'totalPracticesOrActivitiesCompleted'
        ) {
          if (dataSet[key] > 0) {
            numberOfGradesWithProgress++
          }
          pieData.push({ name: key, value: dataSet[key] })
        }
      }
    })
    if (pieData.length === 2 && !isK8Tier2) {
      const indexOfTotalCompleted = pieData.findIndex(
        item => item.name === 'totalPracticesOrActivitiesCompleted',
      )
      pieData = pieData.map(item => {
        if (item.name === 'totalPracticesOrActivities') {
          if (item.value === 0) {
            return { ...item, value: 1 }
          }
          if (item.value > 0) {
            return {
              ...item,
              value: item.value - pieData[indexOfTotalCompleted]?.value,
            }
          }
        } else {
          return item
        }
      })
    }
    // For K8Tier2, ensure chart renders even when all values are zero
    // by setting all segments to equal small values so chart renders as full donut
    if (isK8Tier2 && pieData.length > 0) {
      const allValuesZero = pieData.every(item => item.value === 0)
      if (allValuesZero) {
        pieData = pieData.map(item => ({ ...item, value: 0.01 }))
      }
    }
    return pieData
  }

  const creatUserCountText = (dataSet, programName) => {
    if (programName === 'K-8 Tier 2 Grade Band') {
      return {
        before: `Grades ${dataSet?.gradeBand}`,
        after: `${dataSet?.groupsCount || 0} Groups`,
      }
    }
    return {
      before: `**${programName}**`,
      after: `${dataSet?.totalUsers} Users`,
    }
  }

  const completedText = createCompletedText(data, programName)
  const percentText = createPercentText(data, programName)
  const pieData = createPieData(data, program)
  const userCountText = creatUserCountText(data, programName)
  const blendStroke =
    isK8Tier2 ||
    !isStudentActivities ||
    (isStudentActivities && numberOfGradesWithProgress <= 1)
  const paddingAngle = isK8Tier2
    ? 1
    : isStudentActivities && numberOfGradesWithProgress > 1
    ? 1
    : 0

  const totalStudentActivitiesCompleted =
    data?.totalPracticesOrActivitiesCompleted
  const studentActivitiesEmptyMessage = `${totalStudentActivitiesCompleted} Student Activities Complete`
  const hasNoStudentActivitiesProgress = totalStudentActivitiesCompleted === 0
  const shouldRenderStudentActivitiesEmptyState =
    programName === 'Student Activities' &&
    !isOptedInStudentActivities &&
    hasNoStudentActivitiesProgress

  const handleNavigationClick = () => {
    if (isK8Tier2 && data?.gradeBand && siteId) {
      navigationEvent(`${program?.url}/${data.gradeBand}?site=${siteId}`, PUSH)
    } else {
      navigationEvent(
        `${program?.url}?${TABLE_URL_PARAMS.SITE_ID}=${siteId}`,
        PUSH,
      )
    }
  }

  const shouldShowYellowDot =
    isK8Tier2 && data?.groupsCount > (data?.lessonsCompleted || 0)

  return (
    <ChartWrapper>
      <UserCountButton
        dataTestId={`button-go-to-${programName}`}
        isK8Tier2={isK8Tier2}
        onClick={handleNavigationClick}
      >
        {shouldShowYellowDot && <YellowBullet />}
        <UserCount>{userCountText.before}</UserCount>
        <UserCountSeparator>•</UserCountSeparator>
        <UserCountAfter>{userCountText.after}</UserCountAfter>
        <UserCountArrow alt="Next" src={nextArrowIcon} />
      </UserCountButton>
      <DonutWrapper>
        {!shouldRenderStudentActivitiesEmptyState && (
          <TextWrapper>
            <PercentText>{percentText}</PercentText>
            <CompletedText>{completedText}</CompletedText>
          </TextWrapper>
        )}
        {shouldRenderStudentActivitiesEmptyState && (
          <StudentActivitiesEmptyStateWrapper>
            <StudentActivitiesCompleteWrapper>
              <StyledBullet />
              <StudentActivitiesText>
                {studentActivitiesEmptyMessage}
              </StudentActivitiesText>
            </StudentActivitiesCompleteWrapper>
            <StudentActivitiesEmptyMessageWrapper>
              {STUDENT_ACTIVITIES_EMPTY_STATE_MESSAGE}
            </StudentActivitiesEmptyMessageWrapper>
          </StudentActivitiesEmptyStateWrapper>
        )}

        {!shouldRenderStudentActivitiesEmptyState && (
          <ResponsiveContainer aspect={1.5} width="99%">
            <PieChart>
              <Pie
                blendStroke={blendStroke}
                cx="50%"
                cy="50%"
                data={pieData}
                dataKey="value"
                endAngle={-270}
                fill="#8884d8"
                innerRadius="80%"
                outerRadius="97%"
                paddingAngle={paddingAngle}
                startAngle={90}
              >
                {pieData.map((entry, index) => (
                  <Cell
                    fill={program.colors[index % program.colors.length]}
                    key={`cell-${index}`}
                  />
                ))}
              </Pie>
              <Tooltip
                content={
                  <AdminDonutTooltip data={data} programName={programName} />
                }
              />
            </PieChart>
          </ResponsiveContainer>
        )}
      </DonutWrapper>
      {!shouldRenderStudentActivitiesEmptyState && (
        <CompletedTextAlt>{completedText}</CompletedTextAlt>
      )}
    </ChartWrapper>
  )
}

AdminDonutChart.propTypes = {
  data: PropTypes.oneOfType([
    PropTypes.shape({
      totalPracticesOrActivities: PropTypes.number.isRequired,
      totalPracticesOrActivitiesCompleted: PropTypes.number.isRequired,
      totalUsers: PropTypes.number.isRequired,
    }),
    PropTypes.shape({
      grade10LessonsCompleted: PropTypes.number.isRequired,
      grade11LessonsCompleted: PropTypes.number.isRequired,
      grade12LessonsCompleted: PropTypes.number.isRequired,
      grade9LessonsCompleted: PropTypes.number.isRequired,
      totalUsers: PropTypes.number.isRequired,
    }),
    PropTypes.shape({
      unit1LessonsCompleted: PropTypes.number,
      unit2LessonsCompleted: PropTypes.number,
      unit3LessonsCompleted: PropTypes.number,
      lessonsCompleted: PropTypes.number,
      groupsCount: PropTypes.number,
      totalUsers: PropTypes.number,
      gradeBand: PropTypes.string,
    }),
  ]).isRequired,
  isOptedInStudentActivities: PropTypes.bool,
  navigationEvent: PropTypes.func,
  programName: PropTypes.string.isRequired,
  siteId: PropTypes.number,
}

export default AdminDonutChart
